// src/components/ChangePasswordItem.jsx
// Self-service "Change Password" for ANY logged-in user.
// Drop into the Sidebar's list: <ChangePasswordItem collapsed={isCollapsed} />
import React, { useState } from "react";
import {
    ListItemButton, ListItemIcon, ListItemText, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, InputAdornment, IconButton, Alert, Box, Snackbar,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import { getApiUrl } from "../utils/api";

export default function ChangePasswordItem({ collapsed = false }) {
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState({ c: false, n: false, r: false });
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(false);

    const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setError(""); setShow({ c: false, n: false, r: false }); };
    const close = () => { if (!saving) { setOpen(false); reset(); } };

    const submit = async () => {
        setError("");
        if (!current) return setError("Enter your current password.");
        if (next.length < 6) return setError("New password must be at least 6 characters.");
        if (next !== confirm) return setError("New passwords do not match.");
        if (next === current) return setError("New password must be different from the current one.");

        setSaving(true);
        try {
            // axios default Authorization header is set by AuthContext on login
            await axios.post(getApiUrl("/auth/change-password"), { currentPassword: current, newPassword: next });
            setOpen(false);
            reset();
            setToast(true);
        } catch (e) {
            setError(e.response?.data?.error || "Could not change password. Check your current password.");
        } finally {
            setSaving(false);
        }
    };

    const field = (label, val, setVal, key) => (
        <TextField
            label={label}
            type={show[key] ? "text" : "password"}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            fullWidth
            margin="dense"
            variant="outlined"
            onKeyDown={(e) => e.key === "Enter" && submit()}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton edge="end" onClick={() => setShow((s) => ({ ...s, [key]: !s[key] }))} tabIndex={-1}>
                            {show[key] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );

    return (
        <>
            <Tooltip title={collapsed ? "Change Password" : ""} placement="right" arrow>
                <ListItemButton
                    onClick={() => setOpen(true)}
                    sx={{
                        color: "#e2e8f0",
                        justifyContent: collapsed ? "center" : "flex-start",
                        px: collapsed ? 0 : 2,
                        "&:hover": { backgroundColor: "rgba(96,165,250,0.12)", color: "#60a5fa" },
                    }}
                >
                    <ListItemIcon sx={{ color: "inherit", minWidth: 0, mr: collapsed ? 0 : 2, justifyContent: "center" }}>
                        <LockResetIcon />
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary="Change Password" />}
                </ListItemButton>
            </Tooltip>

            <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: "#0D47A1" }}>Change Password</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 0.5 }}>
                        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                        {field("Current password", current, setCurrent, "c")}
                        {field("New password", next, setNext, "n")}
                        {field("Confirm new password", confirm, setConfirm, "r")}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={close} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} variant="contained"
                        sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#0D47A1", "&:hover": { bgcolor: "#0b3c8a" } }}>
                        {saving ? "Saving…" : "Update Password"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={toast} autoHideDuration={3500} onClose={() => setToast(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity="success" onClose={() => setToast(false)}>Password changed successfully.</Alert>
            </Snackbar>
        </>
    );
}